import React from 'react';

import Button from '../../../../library/button/button';
import Tooltip from '../../../../library/tooltip/tooltip';
import Typography from '../../../../library/typography/typography';
import ProductAvailability from '../../../../components/product-availability/product-availability';
import {
  MapMarkerAltSolidIcon,
  ScrewdriverWrenchRegularIcon,
  ShopSolidIcon,
  TruckSolidIcon
} from '../../../../shared/icons/icons';

import { useAemContent, useMyStore } from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { getPdpMultiStoreDataLayerValues } from '../../utils/product-details-page-analytics-utils';

import {
  BundleData,
  ProductData,
  StockData
} from '../../../../shared/types/graphql-types';
import {
  AEM_CONTENT_IDS,
  AEM_CONTENT_PAGE_IDS
} from '../../../../shared/constants/cms';

import styles from './store-availability-options.scss';

// Remove when OW-17564 is merged
interface ProductDataWithNearbyStoreStock extends ProductData {
  nearByStoreStock: StockData;
}

interface StoreAvailabilityOptionsProps {
  myStoreCode?: string | null;
  onClickStore: (storeCode: string) => void;
  prebuiltPackage?: BundleData;
  product: ProductDataWithNearbyStoreStock;
  quantity?: number;
  rearProduct?: ProductData | null;
  rearQuantity?: number;
  selectedStoreCode?: string | null;
}

function StoreAvailabilityOptions(props: StoreAvailabilityOptionsProps) {
  const {
    myStoreCode,
    onClickStore,
    prebuiltPackage,
    product,
    quantity,
    rearProduct,
    rearQuantity,
    selectedStoreCode
  } = props;

  const nearbyStoreCode = product?.nearByStoreStock?.storeCode;
  const { myStore } = useMyStore();

  const { aemTooltipsContent } = useAemContent(AEM_CONTENT_PAGE_IDS.global);

  const inStockTooltip =
    aemTooltipsContent?.[AEM_CONTENT_IDS.inStockNearbyStore]?.tipText;

  const shouldShowInStockBanner =
    product?.productType === 'Tire' &&
    product?.stock?.availabilityMessageRank !== 1 &&
    (product?.nearByStoreStock?.availabilityMessageRank === 1 ||
      !product?.nearByStoreStock?.availabilityMessageRank);

  if (!myStoreCode) return null;

  function onClickSelectedStoreAnalytics(storeCode) {
    const payload = getPdpMultiStoreDataLayerValues(
      product,
      myStore?.nearByStores,
      myStoreCode,
      storeCode
    );
    trackAction('pdp_multistore_select_store', {
      ...payload
    });
  }

  function handleOnClickPreferredStore() {
    myStoreCode && onClickStore(myStoreCode);
    if (selectedStoreCode !== myStoreCode) {
      onClickSelectedStoreAnalytics(myStoreCode);
    }
  }

  function handleOnClickNearByStore() {
    nearbyStoreCode && onClickStore(nearbyStoreCode);
    if (selectedStoreCode !== nearbyStoreCode) {
      onClickSelectedStoreAnalytics(nearbyStoreCode);
    }
  }

  return (
    <div
      className={`${styles['container']} ${
        !nearbyStoreCode ? styles['container--single-column'] : ''
      } ${styles['container--items-end']}`}
    >
      <Button
        className={`${styles['option']} ${
          selectedStoreCode === myStoreCode ? styles['option--selected'] : ''
        } ${styles['option--no-padding']}`}
        onClick={handleOnClickPreferredStore}
      >
        <div className={styles['option-content']}>
          <Typography
            className={styles['option-title']}
            tag="div"
            weight="bold"
          >
            <MapMarkerAltSolidIcon className={styles['my-store-icon']} />
            My Preferred Store
          </Typography>

          <ProductAvailability
            iconAvailable={<TruckSolidIcon className={styles['truck-icon']} />}
            prebuiltPackage={prebuiltPackage}
            product={product}
            quantity={quantity}
            rearProduct={rearProduct}
            rearQuantity={rearQuantity}
          />
        </div>
      </Button>

      {nearbyStoreCode && (
        <Button
          className={`${styles['option']} ${
            selectedStoreCode === nearbyStoreCode
              ? styles['option--selected']
              : ''
          } ${styles['option--no-padding']}`}
          onClick={handleOnClickNearByStore}
        >
          {shouldShowInStockBanner && (
            <div className={styles['option-banner']}>
              <ScrewdriverWrenchRegularIcon
                className={styles['screwdriver-wrench-icon']}
              />
              Shop <strong>in-stock tires</strong> for{' '}
              <strong>same-day install</strong>
              <Tooltip className={styles['tooltip']} content={inStockTooltip} />
            </div>
          )}
          <div className={styles['option-content']}>
            <Typography
              className={styles['option-title']}
              tag="div"
              weight="bold"
            >
              <ShopSolidIcon className={styles['nearby-store-icon']} />
              Nearby Store
            </Typography>

            <ProductAvailability
              prebuiltPackage={prebuiltPackage}
              product={product}
              quantity={quantity}
              rearProduct={rearProduct}
              rearQuantity={rearQuantity}
              shouldShowNearbyStoreStock
            />
          </div>
        </Button>
      )}
    </div>
  );
}

export default StoreAvailabilityOptions;
